import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Pencil, Trash2 } from "lucide-react";
import SearchIcon from "@mui/icons-material/Search";
import CircularProgress from "@mui/material/CircularProgress";
import CustomPagination from "../../components/Manual_List/Pagination";
import ItemsPerPageSelect from "../../components/Manual_List/ItemsPerPage";
import { SelectChangeEvent } from "@mui/material/Select";
import { ConfirmationModal } from "../../components/shared/ConfrimationModal";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PackageListHeader from "../../components/Headers/PackageListHeader";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  fetchFullPackageById,
  fetchContentTypes,
  deletePackageContent,
} from "../../components/Manual_List/fetch/slices";
import {
  RootState,
  AppDispatch,
} from "../../components/Manual_List/fetch/store";
import { FullPackageData, Language, PackageContent } from "../../Types/types";
import { useLanguage } from "../../components/LanguageContext";
import { stepListTranslations } from "./stepListTranslations";

const StepList: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const dispatch: AppDispatch = useDispatch();
  const location = useLocation();
  const { state } = location;
  const {
    names,
    descriptions,
    selectedCategory,
    subcategories,
    thumbnail,
    steps,
    page: savedPage,
    itemsPerPage: savedItemsPerPage,
    typeFilter: savedTypeFilter,
    searchTerm: savedSearchTerm,
  } = state || {};

  const [typeFilter, setTypeFilter] = useState<string>(savedTypeFilter || "");
  const [searchTerm, setSearchTerm] = useState<string>(savedSearchTerm || "");
  const [itemsPerPage, setItemsPerPage] = useState<number>(
    savedItemsPerPage || 10
  );
  const [page, setPage] = useState<number>(savedPage || 1);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
    useState<boolean>(false);
  const [stepToDelete, setStepToDelete] = useState<PackageContent | null>(null);

  const fullPackage = useSelector(
    (state: RootState) => state.data.fullPackage.data
  );
  const fullPackageStatus = useSelector(
    (state: RootState) => state.data.fullPackage.status
  );
  const contentTypes = useSelector(
    (state: RootState) => state.data.contentTypes.data
  );
  const { language } = useLanguage();
  const t = stepListTranslations[language as Language];

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const fetchStepsData = useCallback(() => {
    if (id) {
      dispatch(fetchFullPackageById({ id, language }));
    }
    dispatch(fetchContentTypes(language));
  }, [dispatch, id, language]);

  useEffect(() => {
    fetchStepsData();
  }, [fetchStepsData, language]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handleTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setTypeFilter(event.target.value);
    setPage(1);
  };

  const handleItemsPerPageChange = (event: SelectChangeEvent<number>) => {
    setItemsPerPage(event.target.value as number);
    setPage(1);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const openConfirmationModal = (step: PackageContent) => {
    setStepToDelete(step);
    setIsConfirmationModalOpen(true);
  };

  const handleNavigateToAddPackage = () => {
    navigate(`/add-package/${id}`, {
      state: {
        names,
        descriptions,
        selectedCategory,
        subcategories,
        thumbnail,
      },
    });
  };

  const handleNavigateToAddStep = () => {
    if (id) {
      dispatch(fetchFullPackageById({ id, language })).then((result) => {
        if (result.payload) {
          const packageData = result.payload as FullPackageData;
          navigate(`/create-step/${id}`, {
            state: {
              id,
              names,
              descriptions,
              selectedCategory,
              subcategories,
              thumbnail,
              steps: packageData.packageContents.map((content) => content.step),
            },
          });
        }
      });
    }
  };

  const handleNavigateToEditStep = (stepId: number) => {
    if (id) {
      dispatch(fetchFullPackageById({ id, language })).then((result) => {
        if (result.payload) {
          const packageData = result.payload as FullPackageData;
          navigate(`/edit-step/${stepId}`, {
            state: {
              packageId: id,
              stepId: stepId,
              steps: packageData.packageContents.map((content) => content.step),
            },
          });
        }
      });
    }
  };

  const handleDeleteConfirmation = () => {
    if (stepToDelete && id) {
      dispatch(deletePackageContent(stepToDelete.id)).then(() => {
        setIsConfirmationModalOpen(false);
        fetchStepsData();
      });
    }
  };

  if (fullPackageStatus === "loading") {
    return (
      <div className="absolute inset-0 bg-gray-100 bg-opacity-75 flex justify-center items-center z-50">
        <CircularProgress />
      </div>
    );
  }

  const stepsData = fullPackage?.packageContents || steps || [];

  const filteredSteps = stepsData.filter((step: PackageContent) => {
    return (
      (typeFilter === "" || step.type === typeFilter) &&
      (searchTerm === "" ||
        step.label.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  });

  const startIndex = (page - 1) * itemsPerPage;
  const paginatedSteps = filteredSteps.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const pageCount = Math.ceil(filteredSteps.length / itemsPerPage);

  const capitalizeFirstLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  };

  return (
    <div className="min-h-screen w-screen flex flex-col bg-gray-100 dark:bg-darkBackground dark:text-white">
      <PackageListHeader />
      <div className="flex flex-col items-center justify-start flex-grow relative">
        <div className="w-full px-4 sm:px-6 md:px-8 py-6 max-w-7xl flex flex-col">
          <h1 className="text-2xl font-semibold mb-6">{t.stepListTitle}</h1>
          <div
            className={`flex ${
              isSmallScreen || isMediumScreen
                ? "flex-col space-y-4 w-full"
                : "justify-between items-center"
            } mb-6`}
          >
            <div
              className={`flex ${
                isSmallScreen || isMediumScreen ? "gap-2 w-full" : "space-x-3"
              }`}
            >
              <button
                type="button"
                className={`bg-gray-300 text-gray-700 dark:bg-white dark:text-black ${
                  isSmallScreen || isMediumScreen
                    ? "px-4 py-2.5 w-full"
                    : "px-6 py-2.5"
                } rounded-lg font-medium shadow-sm hover:bg-opacity-90 transition-all`}
                onClick={handleNavigateToAddPackage}
              >
                {t.package}
              </button>
              <button
                type="button"
                className={`bg-purple text-white ${
                  isSmallScreen || isMediumScreen
                    ? "px-4 py-2.5 w-full"
                    : "px-6 py-2.5"
                } rounded-lg font-medium shadow-sm hover:bg-opacity-90 transition-all`}
              >
                {t.stepsButton}
              </button>
            </div>
            <div
              className={`flex ${
                isSmallScreen || isMediumScreen
                  ? "flex-col space-y-3 w-full"
                  : "space-x-3 items-center"
              }`}
            >
              <select
                className="px-4 py-3 border border-gray-300 rounded-lg w-full sm:w-64 outline-none dark:bg-darkBackground dark:border-gray-600 focus:border-purple focus:ring-1 focus:ring-purple transition-colors shadow-sm"
                value={typeFilter}
                onChange={handleTypeChange}
              >
                <option value="">{t.typeLabel}</option>
                {contentTypes.map((contentType) => (
                  <option key={contentType.id} value={contentType.name}>
                    {capitalizeFirstLetter(contentType.name)}
                  </option>
                ))}
              </select>
              <div className="relative w-full sm:w-64">
                <input
                  type="text"
                  placeholder={t.searchPlaceholder}
                  className="px-4 py-3 border border-gray-300 rounded-lg w-full pr-12 outline-none dark:bg-darkBackground dark:border-gray-600 focus:border-purple focus:ring-1 focus:ring-purple transition-colors shadow-sm"
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
                <SearchIcon className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
              </div>
              <button
                className={`bg-purple text-white ${
                  isSmallScreen || isMediumScreen
                    ? "px-4 py-2.5 w-full"
                    : "px-6 py-2.5 w-48"
                } rounded-lg font-medium shadow-sm hover:bg-opacity-90 transition-all`}
                onClick={handleNavigateToAddStep}
              >
                {t.addStepButton}
              </button>
            </div>
          </div>
          {paginatedSteps != 0 && (
            <div className="overflow-x-auto rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <table className="min-w-full">
                <thead className="bg-gray-50 dark:bg-darkSecondaryBackground border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="py-4 px-6 font-semibold text-left text-sm text-gray-700 dark:text-gray-300">
                      {t.name}
                    </th>
                    <th className="py-4 px-6 font-semibold text-left text-sm text-gray-700 dark:text-gray-300">
                      {t.typeLabel}
                    </th>
                    <th className="py-4 px-6 font-semibold text-right text-sm text-gray-700 dark:text-gray-300">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody className="bg-white dark:bg-darkSecondaryBackground divide-y divide-gray-200 dark:divide-gray-700">
                  {paginatedSteps.map((step: PackageContent) => (
                    <tr
                      key={step.id}
                      className="group hover:bg-purple hover:bg-opacity-5 dark:hover:bg-purple dark:hover:bg-opacity-10 transition-all duration-200 cursor-pointer"
                    >
                      <td className="py-4 px-6 text-left text-gray-900 dark:text-gray-100 font-medium">
                        {step.label}
                      </td>
                      <td className="py-4 px-6 text-left text-gray-600 dark:text-gray-400">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple bg-opacity-10 text-purple dark:bg-purple dark:bg-opacity-20 group-hover:bg-opacity-20 dark:group-hover:bg-opacity-30 transition-all">
                          {capitalizeFirstLetter(step.type)}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-right">
                        <div className="flex justify-end items-center space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNavigateToEditStep(step.id);
                            }}
                            className="p-2 hover:bg-blue-50 dark:hover:bg-blue-900 dark:hover:bg-opacity-20 rounded-lg transition-all duration-200 group/edit"
                            title="Edit step"
                          >
                            <Pencil
                              className="text-gray-400 dark:text-gray-500 group-hover/edit:text-blue-600 dark:group-hover/edit:text-blue-400 group-hover/edit:scale-110 transition-all"
                              size={18}
                            />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openConfirmationModal(step);
                            }}
                            className="p-2 hover:bg-red-50 dark:hover:bg-red-900 dark:hover:bg-opacity-20 rounded-lg transition-all duration-200 group/delete"
                            title="Delete step"
                          >
                            <Trash2
                              className="text-gray-400 dark:text-gray-500 group-hover/delete:text-red-500 dark:group-hover/delete:text-red-400 group-hover/delete:scale-110 transition-all"
                              size={18}
                            />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {paginatedSteps.length === 0 && (
            <div className="text-center py-12 bg-white dark:bg-darkSecondaryBackground rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mt-2">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <p className="mt-4 text-lg font-medium text-gray-500 dark:text-gray-400">
                No data matches this type
              </p>
              <p className="mt-2 text-sm text-gray-400 dark:text-gray-500">
                Try adjusting your filters or add a new step
              </p>
            </div>
          )}
          <div className="flex justify-between items-center pt-6 w-full">
            <CustomPagination
              page={page}
              pageCount={pageCount}
              onPageChange={handlePageChange}
            />
            <ItemsPerPageSelect
              itemsPerPage={itemsPerPage}
              handleItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
        </div>
      </div>
      {stepToDelete && (
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          onClose={() => setIsConfirmationModalOpen(false)}
          onConfirm={handleDeleteConfirmation}
          label={t.deleteStepConfirmation.replace(
            "{label}",
            stepToDelete.label
          )}
        />
      )}
    </div>
  );
};

export default StepList;
